import { environment } from './environments/environment'
import { enableProdMode, importProvidersFrom, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core'
import { provideRouter, Routes, withInMemoryScrolling } from '@angular/router'
import { LoginComponent } from './app/login/login.component'
import { LoginGuard } from './app/guards/login.guard'
import { IlsNotFoundComponent } from './app/ils-not-found/ils-not-found.component'
import { IlsGuard } from './app/guards/ils.guard'
import { IlsPlayerComponent } from './app/ils-player/ils-player.component'
import { AppComponent } from './app/app.component'
import { bootstrapApplication } from '@angular/platform-browser'
import { HTTP_INTERCEPTORS, HttpBackend, HttpClient, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http'
import { AuthInterceptor } from './app/shared/interceptors/auth.interceptor'
import { AUTH_CONFIG, TokenService } from '@monorepo/auth'
import { ANGULAR_COMMON_CONFIG, IlsModelPartsService, NEXTLAB_ACTOR_PROVIDER } from '@monorepo/angular-common'
import { provideTranslateService, TranslateLoader, TranslationObject, TranslateService } from '@ngx-translate/core'
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog'
import { provideApiConfiguration } from '../../../libs/angular/golab-generated/src/lib/api-configuration'
import { IlsModelService } from './app/shared/services/ils-model.service'
import { catchError, firstValueFrom } from 'rxjs'
import { NgxSpinnerModule } from 'ngx-spinner'

if (environment.production) {
  enableProdMode()
}

class IlsPlayerTranslateLoader implements TranslateLoader {
  private readonly localEnglishTranslationsUrl = './assets/i18n/en.json'
  private readonly httpClient: HttpClient

  constructor(http: HttpBackend) {
    this.httpClient = new HttpClient(http)
  }

  getTranslation(lang: string) {
    if (lang === 'en') {
      return this.httpClient.get<TranslationObject>(this.localEnglishTranslationsUrl)
    }

    const translationServerUrl = `${environment.translationServiceURL}/import-json-translations/generate?appIdentifier=ilsPlayer&languageCode=${lang}`

    return this.httpClient.get<TranslationObject>(translationServerUrl).pipe(
      catchError((error) => {
        console.error('Something went wrong for the following translation file:', translationServerUrl, error)
        return this.httpClient.get<TranslationObject>(this.localEnglishTranslationsUrl)
      }),
    )
  }
}

export function createTranslateLoader(http: HttpBackend) {
  return new IlsPlayerTranslateLoader(http)
}

export const appRoutes: Routes = [
  {
    path: 'login/:ilsId',
    component: LoginComponent,
    canActivate: [LoginGuard],
  },
  {
    path: 'error',
    component: IlsNotFoundComponent,
    canActivate: [],
  },
  {
    path: '',
    children: [
      {
        path: '',
        component: IlsNotFoundComponent,
        canActivate: [IlsGuard],
      },
      {
        path: ':ilsId',
        component: IlsPlayerComponent,
        canActivate: [IlsGuard],
      },
    ],
  },
]

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(), {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: AUTH_CONFIG,
      useValue: {
        appName: 'ils-player',
        profileKey: environment.profileKey,
        useLocalStorage: true,
        accessTokenKey: environment.accessTokenKey,
      },
    },
    { provide: NEXTLAB_ACTOR_PROVIDER, useExisting: TokenService },
    {
      provide: ANGULAR_COMMON_CONFIG,
      useValue: {
        serverUrl: environment.serverURL,
        translationServiceURL: environment.translationServiceURL,
        appName: 'ilsPlayer',
        playerUrl: environment.playerURL,
        editorUrl: environment.editorURL,
      },
    },
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { disableClose: true } },
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideRouter(appRoutes, withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })),
    provideTranslateService({
      fallbackLang: 'en',
      lang: 'en',
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpBackend],
      },
    }),
    // Block bootstrap until the default-language translations are loaded, so the
    // UI never flashes raw translation keys before en.json arrives. Errors are
    // swallowed so a slow/unavailable translation source can't prevent boot.
    provideAppInitializer(() => {
      const translate = inject(TranslateService)
      return firstValueFrom(translate.use('en')).catch((error) =>
        console.error('Failed to preload en translations before bootstrap', error),
      )
    }),
    {
      provide: IlsModelPartsService,
      useExisting: IlsModelService,
    },
    provideApiConfiguration(environment.serverURL),
    importProvidersFrom(NgxSpinnerModule.forRoot({ type: 'ball-newton-cradle' })),
  ],
})
